import type { TcForecastOverride } from '../atoms';
import type {
  TypeCurveProbitMetric,
  TypeCurveProbitParamKey,
  TypeCurveCumulativeYears,
} from 'types/type-curve-probit';

/**
 * @param {number} n - The number to round.
 * @return {number} Rounded to 2 decimal places.
 */
function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Compute the `typeCurveValues` object for a probit request based on
 * the current metric selection and forecast override data.
 *
 * Returns `undefined` when the override doesn't apply (wrong TC, missing
 * data for the selected metric) — callers should omit `typeCurveValues`
 * from the payload so the backend falls back to DB lookup.
 * @param {TcForecastOverride | null} override - The forecast override atom value.
 * @param {TypeCurveProbitMetric} metric - The current probit metric selection.
 * @param {number | null} typeCurveHeaderDetailsId - The active TC header details ID.
 * @param {object} [opts] - Optional parameters for metric-specific computation.
 * @return {Record<string, number> | undefined} TC values keyed by stream/param, or undefined.
 */
export function computeTypeCurveValues(
  override: TcForecastOverride | null,
  metric: TypeCurveProbitMetric,
  typeCurveHeaderDetailsId: number | null,
  opts?: {
    paramKey?: TypeCurveProbitParamKey;
    cumulativeYears?: TypeCurveCumulativeYears;
    lateralLengthFt?: number | null;
  }
): Record<string, number> | undefined {
  // Only use override when it matches the current type curve
  if (
    !override ||
    !typeCurveHeaderDetailsId ||
    override.typeCurveHeaderDetailsId !== typeCurveHeaderDetailsId
  ) {
    return undefined;
  }

  switch (metric) {
    case 'eur':
    case 'scaled_eur':
      if (!override.eur) return undefined;
      return { ...override.eur };

    case 'eur_per_ft': {
      const ll = opts?.lateralLengthFt;
      if (!override.eur || !ll || ll <= 0) return undefined;
      const eurFt: Record<string, number> = {};
      if (override.eur.oil !== undefined)
        eurFt.oil = round2((override.eur.oil * 1000.0) / ll);
      if (override.eur.gas !== undefined)
        eurFt.gas = round2((override.eur.gas * 1000.0) / ll);
      if (override.eur.water !== undefined)
        eurFt.water = round2((override.eur.water * 1000.0) / ll);
      return Object.keys(eurFt).length > 0 ? eurFt : undefined;
    }

    case 'dca_params': {
      const pk = opts?.paramKey;
      if (!pk || !override.dcaParams) return undefined;
      const result: Record<string, number> = {};
      for (const stream of ['oil', 'gas', 'water']) {
        const streamParams = override.dcaParams[stream];
        if (!streamParams) continue;
        const val = streamParams[pk as keyof typeof streamParams];
        if (val === null || val === undefined) continue;
        result[stream] = val;
      }
      return Object.keys(result).length > 0 ? result : undefined;
    }

    case 'cumulative': {
      const years = opts?.cumulativeYears;
      if (!years || !override.cumulatives?.length) return undefined;
      const targetMonth = years * 12;
      // Find the record at or closest after the target month
      const row = override.cumulatives.find(
        (c) =>
          c.month !== null && c.month !== undefined && c.month >= targetMonth
      );
      if (!row) return undefined;
      const result: Record<string, number> = {};
      if (row.oil !== null && row.oil !== undefined) result.oil = row.oil;
      if (row.gas !== null && row.gas !== undefined) result.gas = row.gas;
      if (row.water !== null && row.water !== undefined)
        result.water = row.water;
      return Object.keys(result).length > 0 ? result : undefined;
    }

    case 'ratio': {
      const pk = opts?.paramKey;
      if (!pk || !override.eurRatios) return undefined;
      const val = override.eurRatios[pk];
      if (val === null || val === undefined) return undefined;
      return { [pk]: val };
    }

    default:
      return undefined;
  }
}
